
/**
 * Easy Bundles - Dynamic MRP Price Fix V7
 *
 * Updates:
 * 1. Individual product prices to Shopify MRP
 * 2. Crossed-out bundle original price
 * 3. Order subtotal
 * 4. Displayed discount
 *
 * Keeps Shopify checkout total unchanged.
 * Supports multiple bundles, free gifts and quantities.
 */

(() => {
  "use strict";

  if (window.__easyBundleFixV7) return;
  window.__easyBundleFixV7 = true;

  const ROOT = window.Shopify?.routes?.root || "/";
  const CURRENCY = window.Shopify?.currency?.active || "INR";
  const CACHE_PREFIX = "eb-v7:" + CURRENCY + ":";
  const CACHE_TTL = 5 * 60 * 1000;

  const S = {
    wrapper: ".gbbSummaryPageWrapper",
    bundle: ".gbbSummaryPageBundleDetailsHTML",
    item: ".gbbSummaryPageProductListItem",
    name: ".gbbSummaryPageProductListItemName",
    price: ".gbbSummaryPageProductListItemPrice",
    itemQty: ".gbbSummaryPageProductListItemQty",
    bundleQty: ".gbbSummaryPageBundleQuantityText",
    original: ".gbbSummaryPageBundleOriginalPrice",
    subtotal: ".gbbSummaryPageSubTotalValue",
    discount: ".gbbSummaryPageDiscountAppliedValue",
    total: ".gbbSummaryPageTotalCartValue"
  };

  const $ = (selector, root = document) =>
    root.querySelector(selector);

  const $$ = (selector, root = document) =>
    [...root.querySelectorAll(selector)];

  const apiCache = new Map();
  const pendingPrices = new Map();
  const priceCache = new Map();

  // Stores the Easy Bundles selling price before
  // we replace the row with its original MRP.
  let rowState = new WeakMap();

  const formatter = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: CURRENCY,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  const money = value => formatter.format(value / 100);

  function normalize(value) {
    return String(value || "")
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/&/g, " and ")
      .replace(/[^a-z0-9]+/g, " ")
      .trim()
      .replace(/\s+/g, " ");
  }

  function cents(value) {
    const match = String(value || "")
      .replace(/,/g, "")
      .match(/\d+(?:\.\d+)?/);

    return match
      ? Math.round(Number(match[0]) * 100)
      : null;
  }

  function quantity(value) {
    const n = Number(
      String(value || "").match(/\d+/)?.[0] || 1
    );

    return Number.isFinite(n) ? Math.max(1, n) : 1;
  }

  function setText(el, text) {
    if (el && el.textContent !== text) {
      el.textContent = text;
    }
  }

  function memo(key, loader) {
    if (!apiCache.has(key)) {
      const request = Promise.resolve()
        .then(loader)
        .catch(error => {
          apiCache.delete(key);
          throw error;
        });

      apiCache.set(key, request);
    }

    return apiCache.get(key);
  }

  function handleFrom(item) {
    if (item.handle) return item.handle;

    try {
      const url = new URL(item.url, location.origin);

      return decodeURIComponent(
        url.pathname.match(
          /\/products\/([^/?#]+)/
        )?.[1] || ""
      );
    } catch {
      return "";
    }
  }

  async function searchProducts(query) {
    return memo("search:" + normalize(query), async () => {
      const url = new URL(
        ROOT + "search/suggest.json",
        location.origin
      );

      url.searchParams.set("q", query);
      url.searchParams.set("resources[type]", "product");
      url.searchParams.set("resources[limit]", "10");
      url.searchParams.set(
        "resources[options][unavailable_products]",
        "show"
      );

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error("Search failed: " + response.status);
      }

      const data = await response.json();

      return data.resources?.results?.products || [];
    });
  }

  async function getProduct(handle) {
    return memo("product:" + handle, async () => {
      const response = await fetch(
        ROOT +
        "products/" +
        encodeURIComponent(handle) +
        ".js"
      );

      if (!response.ok) {
        throw new Error("Product request failed");
      }

      return response.json();
    });
  }

  function similarity(a, b) {
    if (a === b) return 1;

    const left = new Set(a.split(" "));
    const right = new Set(b.split(" "));

    const common = [...left].filter(
      word => right.has(word)
    ).length;

    return common / Math.max(
      left.size,
      right.size,
      1
    );
  }

  function matchScore(name, product, variant) {
    const shown = normalize(name);
    const base = normalize(product.title);
    const variantName = normalize(variant.title);

    if (!shown || !base) return 0;

    const full =
      !variantName || variantName === "default title"
        ? base
        : base + " " + variantName;

    return Math.max(
      similarity(shown, base),
      similarity(shown, full)
    );
  }

  async function matchProducts(name, salePrice, handles) {
    const products = await Promise.all(
      [...handles].map(handle =>
        getProduct(handle).catch(() => null)
      )
    );

    const matches = [];

    for (const product of products.filter(Boolean)) {
      for (const variant of product.variants || []) {
        const regular = Number(variant.price);

        if (!Number.isFinite(regular)) continue;

        const isGift = salePrice === 0;

        // Free gifts may have non-zero Shopify prices.
        if (!isGift && regular !== salePrice) {
          continue;
        }

        const score = matchScore(
          name,
          product,
          variant
        );

        if (score < (isGift ? 0.85 : 0.70)) {
          continue;
        }

        const compareAt = Number(
          variant.compare_at_price
        );

        const original =
          Number.isFinite(compareAt) &&
          compareAt > regular
            ? compareAt
            : regular;

        matches.push({
          score,
          original,
          variantId: variant.id
        });
      }
    }

    matches.sort((a, b) => b.score - a.score);

    if (!matches.length) {
      return null;
    }

    const best = matches[0];

    const closeMatches = matches.filter(
      item => best.score - item.score < 0.15
    );

    const prices = [
      ...new Set(
        closeMatches.map(item => item.original)
      )
    ];

    // Only accept variants that agree on MRP.
    if (prices.length !== 1) {
      return null;
    }

    return prices[0];
  }

  function getCachedPrice(key) {
    const memory = priceCache.get(key);

    if (memory?.expires > Date.now()) {
      return memory.price;
    }

    priceCache.delete(key);

    try {
      const saved = JSON.parse(
        sessionStorage.getItem(key)
      );

      if (
        saved?.expires > Date.now() &&
        Number.isFinite(saved.price)
      ) {
        priceCache.set(key, saved);
        return saved.price;
      }
    } catch {}

    return null;
  }

  function savePrice(key, price) {
    const value = {
      price,
      expires: Date.now() + CACHE_TTL
    };

    priceCache.set(key, value);

    try {
      sessionStorage.setItem(
        key,
        JSON.stringify(value)
      );
    } catch {}
  }

  async function originalPrice(name, salePrice) {
    const key = CACHE_PREFIX +
      normalize(name) + ":" + salePrice;

    const saved = getCachedPrice(key);

    if (saved !== null) return saved;

    if (pendingPrices.has(key)) {
      return pendingPrices.get(key);
    }

    const lookup = (async () => {
      const words = name.trim().split(/\s+/);

      const queries = [...new Set([
        name,
        words.slice(0, 5).join(" "),
        words.slice(0, 3).join(" "),
        words.slice(0, 2).join(" ")
      ].filter(Boolean))];

      const handles = new Set();

      for (const query of queries) {
        let results = [];

        try {
          results = await searchProducts(query);
        } catch {
          continue;
        }

        for (const item of results) {
          const handle = handleFrom(item);

          if (handle) handles.add(handle);
        }

        const match = await matchProducts(
          name,
          salePrice,
          handles
        );

        if (match !== null) {
          savePrice(key, match);
          return match;
        }
      }

      throw new Error("Product not matched: " + name);
    })();

    pendingPrices.set(key, lookup);

    try {
      return await lookup;
    } finally {
      pendingPrices.delete(key);
    }
  }

  /*
   * Read product rows.
   *
   * If this script previously replaced a row's sale
   * price with MRP, retain its real sale price for
   * subsequent Shopify variant matching.
   */
  function readItem(row) {
    const name = $(S.name, row)?.textContent.trim() || "";
    const priceElement = $(S.price, row);
    const shownPrice = cents(priceElement?.textContent);

    const previous = rowState.get(row);

    let salePrice = shownPrice;

    if (
      previous &&
      previous.name === name &&
      previous.renderedMRP === shownPrice
    ) {
      salePrice = previous.salePrice;
    }

    return {
      row,
      name,
      sale: salePrice,
      qty: quantity($(S.itemQty, row)?.textContent)
    };
  }

  function readBundles(wrapper) {
    const bundles = $$(S.bundle, wrapper).map(el => ({
      el,
      bundleQty: quantity($(S.bundleQty, el)?.textContent),
      items: $$(S.item, el).map(readItem)
    }));

    const total = cents($(S.total, wrapper)?.textContent);

    const signature = JSON.stringify({
      bundles: bundles.map(bundle => ({
        qty: bundle.bundleQty,
        items: bundle.items.map(item => ({
          name: item.name,
          sale: item.sale,
          qty: item.qty
        }))
      })),
      total
    });

    return { bundles, total, signature };
  }

  let running = false;
  let queued = false;
  let timer;
  let lastSignature = "";
  let lastValues = null;

  function displayCorrect(wrapper, state) {
    if (
      state.signature !== lastSignature ||
      !lastValues
    ) {
      return false;
    }

    const { originals, itemPrices, subtotal, discount } =
      lastValues;

    if (
      $(S.subtotal, wrapper)?.textContent !== money(subtotal) ||
      $(S.discount, wrapper)?.textContent !==
        "-" + money(discount)
    ) {
      return false;
    }

    return state.bundles.every((bundle, bundleIndex) => {
      if (
        $(S.original, bundle.el)?.textContent !==
        money(originals[bundleIndex])
      ) {
        return false;
      }

      return bundle.items.every((item, itemIndex) =>
        $(S.price, item.row)?.textContent ===
        money(itemPrices[bundleIndex][itemIndex])
      );
    });
  }

  async function updatePrices() {
    if (running) {
      queued = true;
      return;
    }

    const wrapper = $(S.wrapper);
    if (!wrapper) return;

    const state = readBundles(wrapper);

    if (!state.bundles.length || state.total === null) {
      return;
    }

    if (displayCorrect(wrapper, state)) {
      return;
    }

    running = true;

    try {
      // Fetch all selected products concurrently.
      const itemPrices = await Promise.all(
        state.bundles.map(async bundle =>
          Promise.all(
            bundle.items.map(async item => {
              if (!item.name || item.sale === null) {
                throw new Error("Invalid product row");
              }

              return originalPrice(
                item.name,
                item.sale
              );
            })
          )
        )
      );

      // Ensure the customer has not changed selection.
      if (
        !wrapper.isConnected ||
        $(S.wrapper) !== wrapper ||
        readBundles(wrapper).signature !== state.signature
      ) {
        queued = true;
        return;
      }

      // Original unit price for each bundle.
      const originals = state.bundles.map(
        (bundle, bundleIndex) =>
          bundle.items.reduce(
            (sum, item, itemIndex) =>
              sum +
              itemPrices[bundleIndex][itemIndex] * item.qty,
            0
          )
      );

      // Combined original subtotal.
      const subtotal = originals.reduce(
        (sum, original, index) =>
          sum +
          original * state.bundles[index].bundleQty,
        0
      );

      if (subtotal < state.total) {
        throw new Error(
          "Invalid total: original subtotal below checkout"
        );
      }

      if (
        !$(S.subtotal, wrapper) ||
        !$(S.discount, wrapper) ||
        state.bundles.some(bundle =>
          !$(S.original, bundle.el)
        ) ||
        state.bundles.some(bundle =>
          bundle.items.some(item => !$(S.price, item.row))
        )
      ) {
        throw new Error("Price display elements missing");
      }

      // 1. Update each individual product price to MRP.
      state.bundles.forEach((bundle, bundleIndex) => {
        bundle.items.forEach((item, itemIndex) => {
          const original =
            itemPrices[bundleIndex][itemIndex];

          const priceElement = $(S.price, item.row);

          rowState.set(item.row, {
            name: item.name,
            salePrice: item.sale,
            renderedMRP: original
          });

          setText(priceElement, money(original));
        });

        // 2. Update crossed-out bundle original price.
        setText(
          $(S.original, bundle.el),
          money(originals[bundleIndex])
        );
      });

      // 3. Update order subtotal.
      setText(
        $(S.subtotal, wrapper),
        money(subtotal)
      );

      // 4. Update displayed discount.
      const discount = subtotal - state.total;

      setText(
        $(S.discount, wrapper),
        "-" + money(discount)
      );

      // 5. Never update actual payable total.
      lastSignature = state.signature;
      lastValues = {
        originals,
        itemPrices,
        subtotal,
        discount
      };

    } catch (error) {
      console.warn(
        "[EasyBundleFix]",
        error.message
      );
    } finally {
      running = false;

      if (queued) {
        queued = false;
        scheduleUpdate();
      }
    }
  }

  function scheduleUpdate() {
    clearTimeout(timer);
    timer = setTimeout(updatePrices, 100);
  }

  function init() {
    const observer = new MutationObserver(records => {
      const relevant = records.some(record => {
        const target = record.target.nodeType === 1
          ? record.target
          : record.target.parentElement;

        if (target?.closest?.(S.wrapper)) {
          return true;
        }

        return [...record.addedNodes].some(node =>
          node.nodeType === 1 &&
          (
            node.matches?.(S.wrapper) ||
            node.querySelector?.(S.wrapper)
          )
        );
      });

      if (relevant) scheduleUpdate();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true
    });

    window.EasyBundlePriceFix = {
      refresh: updatePrices,

      clearCache() {
        apiCache.clear();
        pendingPrices.clear();
        priceCache.clear();

        try {
          Object.keys(sessionStorage)
            .filter(key => key.startsWith(CACHE_PREFIX))
            .forEach(key =>
              sessionStorage.removeItem(key)
            );
        } catch {}

        lastSignature = "";
        lastValues = null;
        scheduleUpdate();
      }
    };

    scheduleUpdate();
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      init,
      { once: true }
    );
  } else {
    init();
  }
})();
